import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import {
  ArrowLeft,
  Share2,
  Calendar,
  Play,
  ChevronUp,
  Trophy,
  AlertCircle,
  MessageSquare,
  Send,
  MoreVertical,
  Trash2,
  ShieldAlert,
  Loader2,
  CornerDownRight,
  X,
  Heart,
  Pause,
  ArrowDown,
  ArrowUp,
  Info,
  MapPin,
  Target,
  Users2,
  Scale,
  Map as MapIcon,
  ExternalLink,
  Sparkles,
  BrainCircuit,
  ShieldCheck,
  Zap,
  Tag,
  Share,
  Camera,
  ImageIcon,
  Scale as RefereeIcon,
  User,
  Download,
  Maximize2,
  RefreshCw,
  Quote,
  History,
} from 'lucide-react'
import {
  Game,
  MatchDetail,
  VideoItem,
  CommentWithUser,
  MatchEvent,
  TeamLineup,
  StandingItem,
  TeamColors,
} from '../../types'
import {
  fetchMatchDetails,
  fetchLeagueStandings,
  fetchVenueDetails,
  fetchPredictions,
  fetchH2H,
  fetchSupabaseNewsList,
  fetchSupabaseNewsItem,
  incrementNewsView,
  getNewsViewCount,
} from '../../services/footballService'
import { fetchMatchHighlightsFromSupabase } from '../../services/highlightService'
import { favoriteService } from '../../services/favoriteService'
import {
  predictMatchResult,
  MatchPredictionData,
} from '../../services/geminiService'
import {
  fetchComments,
  createComment,
  deleteComment,
  likeComment,
  unlikeComment,
  reportComment,
} from '../../services/commentService'
import { supabase, uploadSharedImage } from '../../services/authService'
import { pointService } from '../../services/pointService'
import { LoadingBar } from '../Layout/LoadingBar'
import { FullPageLoader } from '../Common/FullPageLoader'
import { ConfirmModal } from '../Common/ConfirmModal'
import { ReportModal } from '../Common/ReportModal'
import { AIVoiceWave } from '../Common/AIVoiceWave'
import { PointFeedback } from '../Common/PointFeedback'
import { PlayerQuickModal } from '../Games/PlayerQuickModal'
import { TeamQuickModal } from '../Games/TeamQuickModal'
import { StandingsModal } from '../Games/StandingsModal'
import { SquadBuilder } from '../Games/SquadBuilder'
import { MatchScoreHeader } from '../Games/MatchScoreHeader'
import { MatchStatsTab } from '../Games/MatchStatsTab'
import { MatchTimelineTab } from '../Games/MatchTimelineTab'
import { MatchLineupsTab } from '../Games/MatchLineupsTab'
import { MatchPredictionGraph } from '../Games/MatchPredictionGraph'
import { CommentCard } from '../Games/CommentCard'
import { MatchH2HSection } from '../Games/MatchH2HSection'
import ImageZoomModal from '../Common/ImageZoomModal'
import { useLanguage } from '../../context/LanguageContext'
import { useAuth } from '../../context/AuthContext'
import { useVideo } from '../../context/VideoContext'
import { useToast } from '../../context/ToastContext'
import { shareContent } from '../../services/shareService'
import { calculateTimeAgo } from '../../services/formatService'
import { getLeagueName } from '../../services/leagueService'
import { getTeamName } from '../../services/teamService'
import { t } from '../../i18n'
import SEO from '../Common/SEO'

interface GameDetailViewProps {
  gameId: string
  onBack: () => void
}

const LOCAL_SELECTION_KEY = 'xleven_league_session'

export const GameDetailView: React.FC<GameDetailViewProps> = ({
  gameId,
  onBack,
}) => {
  const { lang } = useLanguage()
  const { user } = useAuth()
  const { showToast } = useToast()
  const { activeVideo, setActiveVideo } = useVideo()
  const [detail, setDetail] = useState<MatchDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [activeTab, setActiveTab] = useState<
    'Stats' | 'Line Up' | 'Timeline' | 'Talk'
  >('Line Up')

  const [homeForm, setHomeForm] = useState<string[]>([])
  const [awayForm, setAwayForm] = useState<string[]>([])
  const [matchSeason, setMatchSeason] = useState<string>('')
  const [leagueLogo, setLeagueLogo] = useState<string>('')

  const [comments, setComments] = useState<
    (CommentWithUser & { is_liked?: boolean })[]
  >([])
  const [newComment, setNewComment] = useState('')

  const [attachedImageFile, setAttachedImageFile] = useState<File | null>(null)
  const [attachedImageUrl, setAttachedImageUrl] = useState<string | null>(null)

  const [zoomedImage, setZoomedImage] = useState<string | null>(null)

  const [isPosting, setIsPosting] = useState(false)
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null)
  const [replyingTo, setReplyingTo] = useState<CommentWithUser | null>(null)
  const [isPredicting, setIsPredicting] = useState(false)
  const isPredictingRef = useRef(false)
  const [matchPrediction, setMatchPrediction] =
    useState<MatchPredictionData | null>(null)
  const [apiPredictions, setApiPredictions] = useState<any | null>(null)
  const [h2hData, setH2hData] = useState<any[]>([])

  const [isCommentInView, setIsCommentInView] = useState(false)

  const tabs = useMemo(() => [
    { id: 'Stats', label: t(lang, 'matchStats') },
    { id: 'Line Up', label: t(lang, 'lineUp') },
    { id: 'Talk', label: t(lang, 'talk') },
  ] as const, [lang])

  const [pointFeedback, setPointFeedback] = useState<{ 
    type: 'deduction' | 'insufficient', 
    amount: number,
    position?: { x: number, y: number }
  } | null>(null)

    const isScheduled = detail?.status === 'Scheduled'
    const isLive = detail?.status === 'Live'
    const isFinished = detail?.status === 'Finished'
  
  // 경기 시작 한 시간 전 체크 (또는 이미 진행 중/종료됨)
  const isWithinOneHour = useMemo(() => {
    if (!detail?.dateLabel) return false;
    if (isLive || isFinished) return true;
    
    // Scheduled 상태일 때만 시간 체크
    const matchDateStr = detail.time ? `${detail.dateLabel}T${detail.time}:00` : detail.dateLabel;
    const matchTime = new Date(matchDateStr).getTime();
    const now = Date.now();
    const timeUntilMatch = matchTime - now;
    return timeUntilMatch > 0 && timeUntilMatch <= 3600000;
  }, [detail?.dateLabel, detail?.time, isLive, isFinished]);

  const currentMatchVideo = useMemo(
    () => (activeVideo && activeVideo.matchId === gameId ? activeVideo : null),
    [activeVideo, gameId],
  )

  const structuredComments = useMemo(() => {
    const root = comments
      .filter((c) => !c.parent_id)
      .sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      )
    const replies = comments.filter((c) => c.parent_id)
    return root.map((parent) => ({
      ...parent,
      replies: replies
        .filter((r) => String(r.parent_id) === String(parent.id))
        .sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        ),
    }))
  }, [comments])

  const discussionRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  const commentInputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const commentsEndRef = useRef<HTMLDivElement>(null)

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [aiErrorModalOpen, setAiErrorModalOpen] = useState(false)
  const [isStandingsModalOpen, setIsStandingsModalOpen] = useState(false)
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false)
  const [isVenueAnimating, setIsVenueAnimating] = useState(false)
  const [venueDetails, setVenueDetails] = useState<any>(null)
  const [isVenueLoading, setIsVenueLoading] = useState(false)
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null)
  const [commentToReport, setCommentToReport] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isReporting, setIsReporting] = useState(false)
  const [highlights, setHighlights] = useState<VideoItem[]>([])
  const [selectedPlayerForModal, setSelectedPlayerForModal] =
    useState<any>(null)
  const [selectedTeamForModal, setSelectedTeamForModal] = useState<any>(null)
  const [favoriteModalOpen, setFavoriteModalOpen] = useState(false)
  const [teamToFavorite, setTeamToFavorite] = useState<string | null>(null)
  const [favoriteLoading, setFavoriteLoading] = useState(false)
  const [favoriteSecondaryLoading, setFavoriteSecondaryLoading] = useState(false)
  const [favoriteUpdateTrigger, setFavoriteUpdateTrigger] = useState(0)

  const loadComments = useCallback(async () => {
    setCommentsLoading(true)
    const data = await fetchComments(gameId)
    setComments(data)
    setCommentsLoading(false)
  }, [gameId])

  useEffect(() => {
    if (!user) {
      const existingSession = localStorage.getItem(LOCAL_SELECTION_KEY)
      if (!existingSession) {
        const sessionData = {
          ids: [],
          timestamp: Date.now(),
        }
        localStorage.setItem(LOCAL_SELECTION_KEY, JSON.stringify(sessionData))
      }
    }
  }, [user])

  const load = useCallback(
    async (manual: boolean = false) => {
      if (!manual) setLoading(true)
      else setIsRefreshing(true)
      setError(false)
      try {
        const data = await fetchMatchDetails(gameId, undefined, manual)
        if (data) {
          setDetail(data)
          if (data.leagueLogo) setLeagueLogo(data.leagueLogo)

          if (manual) {
            loadComments()
          }

          const year = new Date(data.dateLabel).getFullYear().toString()
          const month = new Date(data.dateLabel).getMonth()
          const season = month < 6 ? (parseInt(year) - 1).toString() : year
          setMatchSeason(season)

          const subPromises = []
          if (!manual) {
            if (data.league) {
              subPromises.push(
                fetchLeagueStandings(data.league, season).then((standings) => {
                  const hTeam = standings.find(
                    (s) => s.team.id === data.homeTeam.id,
                  )
                  const aTeam = standings.find(
                    (s) => s.team.id === data.awayTeam.id,
                  )
                  if (hTeam?.form) setHomeForm(hTeam.form)
                  if (aTeam?.form) setAwayForm(aTeam.form)
                }),
              )
            }
            if (data.status !== 'Scheduled') {
              subPromises.push(
                fetchMatchHighlightsFromSupabase(
                  data.homeTeam.name,
                  data.awayTeam.name,
                  data.dateLabel,
                  gameId,
                ).then(setHighlights),
              )
            }
          }

          if (data.status === 'Scheduled' && !manual) {
            subPromises.push(
              fetchPredictions(gameId).then((pred) => {
                setApiPredictions(pred)
                const h2hId = pred?.predictions?.h2h || `${data.homeTeam.id}-${data.awayTeam.id}`
                fetchH2H(h2hId, 10).then(setH2hData)
              }),
            )
            if (user) {
              subPromises.push(
                supabase
                  .from('match_predictions')
                  .select('*')
                  .eq('post_id', gameId)
                  .eq('user_id', user.id)
                  .maybeSingle()
                  .then(({ data: existingPred }) => {
                    if (existingPred) setMatchPrediction(existingPred)
                  }),
              )
            }
          }
          if (subPromises.length > 0) await Promise.all(subPromises)
        } else {
          setError(true)
        }
      } catch (e) {
        setError(true)
      } finally {
        setLoading(false)
        setIsRefreshing(false)
      }
    },
    [gameId, lang, user, loadComments],
  )

  // 데이터 부재 시 자동 재시도 로직
  useEffect(() => {
    if (loading || isRefreshing || error || !detail) return;
    
    const isLive = detail.status === 'Live';
    const isFinished = detail.status === 'Finished';
    
    if (isLive || isFinished) {
      const hasNoStats = !detail.stats || detail.stats.length === 0;
      const hasNoLineups = !detail.lineups;
      const hasNoEvents = !detail.events || detail.events.length === 0;
      
      if ((hasNoStats || hasNoLineups || hasNoEvents) && retryCount < 3) {
        const timer = setTimeout(() => {
          setRetryCount(prev => prev + 1);
          load(true); // manual load
        }, 5000); // 5초 후 재시도
        return () => clearTimeout(timer);
      }
    }
  }, [detail, loading, isRefreshing, error, retryCount, load]);

  useEffect(() => {
    // 이미 로딩이 완료되었고 에러가 없다면(뒤로가기로 돌아왔을 때 등) 다시 호출하지 않도록 최적화
    // 단, isLive 상태라면 아래의 setInterval에서 알아서 갱신됨
    if (!detail) {
      load()
      loadComments()
    }
  }, [detail, load, loadComments])

  // 라이브 경기 시 1분마다 데이터 갱신
  useEffect(() => {
    if (detail?.status !== 'Live') return;
    const interval = setInterval(() => {
      load(true);
    }, 60000);
    return () => clearInterval(interval);
  }, [detail?.status, load]);

  useEffect(() => {
    if (!discussionRef.current) return
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        setIsCommentInView(entry.isIntersecting)
      },
      { threshold: 0.1, rootMargin: '0px 0px 100px 0px' },
    )
    observer.observe(discussionRef.current)
    return () => observer.disconnect()
  }, [])

  const scrollToBottom = useCallback(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
    }
  }, [])

  const handleCancelAttachment = useCallback(() => {
    if (attachedImageUrl && attachedImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(attachedImageUrl)
    }
    setAttachedImageFile(null)
    setAttachedImageUrl(null)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [attachedImageUrl])

  const handlePostComment = useCallback(async () => {
    if (!user) {
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname)
      setLoginModalOpen(true)
      return
    }
    if (!newComment.trim() && !attachedImageUrl) return
    setIsPosting(true)
    try {
      let finalImageUrl = attachedImageUrl
      if (attachedImageFile) {
        finalImageUrl = await uploadSharedImage(user.id, attachedImageFile)
      }
      const added = await createComment({
        postId: gameId,
        content: newComment,
        imageUrl: finalImageUrl,
        parentId: replyingTo?.id ? String(replyingTo.id) : null,
      })
      setComments((prev) => [...prev, added])
      setNewComment('')
      if (commentInputRef.current) {
        commentInputRef.current.style.height = 'auto'
      }
      handleCancelAttachment()
      setReplyingTo(null)
      setTimeout(scrollToBottom, 150)
    } catch (e: any) {
      showToast('error', e.message)
    } finally {
      setIsPosting(false)
    }
  }, [user, newComment, attachedImageUrl, attachedImageFile, gameId, replyingTo?.id, scrollToBottom, showToast, handleCancelAttachment])

  const handleImageFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !user) return
    const localPreviewUrl = URL.createObjectURL(file)
    setAttachedImageFile(file)
    setAttachedImageUrl(localPreviewUrl)
    setActiveTab('Talk')
  }, [user])

  const handleShareSquad = useCallback((url: string) => {
    setAttachedImageFile(null)
    setAttachedImageUrl(url)
    setActiveTab('Talk')
    setTimeout(() => {
      discussionRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 300)
  }, [])

  const handleReplyClick = useCallback((comment: CommentWithUser) => {
    if (!user) {
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname)
      setLoginModalOpen(true)
      return
    }
    const parentComment = comment.parent_id
      ? comments.find((c) => String(c.id) === String(comment.parent_id)) ||
        comment
      : comment
    setReplyingTo(parentComment)
    setActiveTab('Talk')
    setTimeout(() => commentInputRef.current?.focus(), 100)
  }, [user, comments])

  const confirmDelete = useCallback((id: string | number) => {
    setCommentToDelete(String(id))
    setDeleteModalOpen(true)
  }, [])

  const executeDelete = useCallback(async () => {
    if (!commentToDelete) return
    setIsDeleting(true)
    try {
      await deleteComment(commentToDelete!)
      setComments((prev) =>
        prev.filter(
          (c) =>
            String(c.id) !== commentToDelete &&
            String(c.parent_id) !== commentToDelete,
        ),
      )
      setDeleteModalOpen(false)
      setActiveMenuId(null)
      setCommentToDelete(null)
    } catch (e) {
      showToast('error', t(lang, 'deleteFailed'))
    } finally {
      setIsDeleting(false)
    }
  }, [commentToDelete, showToast, lang])

  const handleLikeToggle = useCallback(async (
    commentId: string | number,
    isLiked: boolean,
  ) => {
    if (!user) {
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname)
      setLoginModalOpen(true)
      return
    }
    setComments((prev) =>
      prev.map((c) => {
        if (String(c.id) === String(commentId)) {
          const currentLikes = Number(c.likes_count) || 0
          return {
            ...c,
            is_liked: !isLiked,
            likes_count: isLiked
              ? Math.max(0, currentLikes - 1)
              : currentLikes + 1,
          }
        }
        return c
      }),
    )
    try {
      if (isLiked) await unlikeComment(String(commentId))
      else await likeComment(String(commentId))
    } catch (e) {
      loadComments()
    }
  }, [user, loadComments])

  const handleReportClick = useCallback((id: string | number) => {
    if (!user) {
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname)
      setLoginModalOpen(true)
      return
    }
    setCommentToReport(String(id))
    setReportModalOpen(true)
  }, [user])

  const executeReport = useCallback(async (reason: string) => {
    if (!commentToReport) return
    setIsReporting(true)
    try {
      await reportComment(commentToReport, reason)
      showToast('success', t(lang, 'reportCompleted'))
      setReportModalOpen(false)
      setActiveMenuId(null)
      setCommentToReport(null)
    } catch (e) {
      showToast('error', t(lang, 'errorReport'))
    } finally {
      setIsReporting(false)
    }
  }, [commentToReport, lang, showToast])

  const handleLoginRedirect = useCallback(() => {
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname)
    window.navigateTo('/login')
  }, [])

  const displayLeagueName = useMemo(() => {
    if (!detail) return ''
    return getLeagueName(detail.league as unknown as number, lang, detail.leagueName || String(detail.league) || t(lang, 'matchStats'))
  }, [detail, lang])

  const handleShare = useCallback(async () => {
    if (!detail) return
    const home = detail.homeTeam.name
    const away = detail.awayTeam.name
    const score = detail.score || '0-0'

    let title: string
    let text: string

    if (detail.status === 'Live') {
      const elapsed = detail.elapsed || detail.currentMinute || ''
      title = `🔴 LIVE: ${home} vs ${away} | XLEVEN`
      text = elapsed ? `${elapsed}' | ${score} | ${displayLeagueName}` : `${score} | ${displayLeagueName}`
    } else if (detail.status === 'Finished') {
      title = `${home} ${score} ${away} | XLEVEN`
      text = `FT | ${displayLeagueName}`
    } else {
      title = `${home} vs ${away} | XLEVEN`
      const timeStr = detail.time ? ` ${detail.time}` : ''
      text = `${displayLeagueName} | ${detail.dateLabel}${timeStr}`
    }

    await shareContent({ title, text, url: window.location.href }, lang, showToast)
  }, [detail, displayLeagueName, lang, showToast])

  const handleSelectVideo = useCallback((video: VideoItem) => {
    setActiveVideo({
      videoId: video.videoId,
      title: video.title,
      matchId: gameId,
      isPaused: false,
    })
  }, [gameId, setActiveVideo])

  const handleTabClick = useCallback((tabId: 'Stats' | 'Line Up' | 'Timeline' | 'Talk') => {
    setActiveTab(tabId)
    if (tabsRef.current) {
      const headerOffset = 65 // 헤더 높이
      const elementPosition =
        tabsRef.current.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - headerOffset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }, [])

  const handlePlayerClick = useCallback((p: any, teamName: string, teamLogo: string) => {
    if (p.id && !selectedPlayerForModal) {
        setSelectedPlayerForModal({ ...p, teamName, teamLogo, fixtureId: gameId })
    }
  }, [gameId, selectedPlayerForModal])

  const handleCoachClick = useCallback((coach: any, teamName: string, teamLogo: string) => {
    if (!coach?.id) return
    setSelectedPlayerForModal({
      id: coach.id,
      name: coach.name,
      photo: coach.photo,
      teamName,
      teamLogo,
      isCoach: true,
    })
  }, [selectedPlayerForModal])

  const handleTeamClick = useCallback((team: {
    id: string
    name: string
    logo: string
  }) => {
    setSelectedTeamForModal({
      ...team,
      leagueId: detail?.league,
      season: matchSeason,
    })
  }, [detail?.league, matchSeason])

  const handleOpenVenueModal = useCallback(() => {
    setIsVenueModalOpen(true)
    setTimeout(() => setIsVenueAnimating(true), 10)
    if (venueDetails || !detail?.venueId) return
    setIsVenueLoading(true)
    fetchVenueDetails(detail.venueId as number)
      .then(setVenueDetails)
      .catch(console.error)
      .finally(() => setIsVenueLoading(false))
  }, [venueDetails, detail?.venueId])

  const handleCloseVenueModal = useCallback(() => {
    setIsVenueAnimating(false)
    setTimeout(() => setIsVenueModalOpen(false), 500)
  }, [])

  const handleOpenImageZoom = useCallback((url: string) => {
    setZoomedImage(url)
  }, [])

  const handleCloseImageZoom = useCallback(() => {
    setZoomedImage(null)
  }, [])

  const handleToggleFavorite = useCallback((teamId: string) => {
    setTeamToFavorite(teamId)
    setFavoriteModalOpen(true)
  }, [])

  const handleOpenStandings = useCallback(() => {
    setIsStandingsModalOpen(true)
  }, [])

  const handleConfirmFavoriteDefault = useCallback(async () => {
    if (!teamToFavorite) return
    setFavoriteLoading(true)
    try {
      await favoriteService.toggleFavoriteTeam(teamToFavorite, true)
      setFavoriteUpdateTrigger(prev => prev + 1)
    } finally {
      setFavoriteModalOpen(false)
      setFavoriteLoading(false)
      setTeamToFavorite(null)
    }
  }, [teamToFavorite])

  const handleNoFavoriteDefault = useCallback(async () => {
    if (!teamToFavorite) return
    setFavoriteSecondaryLoading(true)
    try {
      await favoriteService.addOnlyFavorite(teamToFavorite)
      setFavoriteUpdateTrigger(prev => prev + 1)
    } finally {
      setFavoriteModalOpen(false)
      setFavoriteSecondaryLoading(false)
      setTeamToFavorite(null)
    }
  }, [teamToFavorite])

  const handleCloseFavoriteModal = useCallback(() => {
    setFavoriteModalOpen(false)
    setTeamToFavorite(null)
  }, [])

  const handlePredictMatch = useCallback(async (e?: React.MouseEvent) => {
    if (!detail || !user) {
      if (!user) {
        sessionStorage.setItem('redirectAfterLogin', window.location.pathname)
        setLoginModalOpen(true)
      }
      return
    }

    if (isPredictingRef.current) return;
    isPredictingRef.current = true;

    let position;
    if (e) {
      const rect = e.currentTarget.getBoundingClientRect();
      position = {
        x: rect.left + rect.width / 2,
        y: rect.top
      };
    }

    setPointFeedback({ type: 'deduction', amount: 10, position });
    const deduction = await pointService.deductPoints(user.id, 10, `AI Report: ${detail.homeTeam.name} vs ${detail.awayTeam.name}`);

    if (!deduction.success) {
      setPointFeedback({ type: 'insufficient', amount: 10, position });
      isPredictingRef.current = false;
      return;
    }

    setIsPredicting(true);
    
    try {
      // 1. 이미 해당 경기에 대한 리포트가 있는지 확인
      const { data: existingReports } = await supabase
        .from('match_predictions')
        .select('*')
        .eq('post_id', gameId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (existingReports && existingReports.length > 0) {
        const existing = existingReports[0];
        
        // 2. 기존 데이터가 있다면 해당 내용을 복사하여 저장
        const predictionData = {
          post_id: gameId,
          user_id: user.id,
          predicted_winner: existing.predicted_winner,
          predicted_score: existing.predicted_score,
          win_probability: existing.win_probability,
          analysis_reason: existing.analysis_reason,
          analysis_reason_ko: existing.analysis_reason_ko,
          // analysis_reason_ja: existing.analysis_reason_ja,
          // analysis_reason_es: existing.analysis_reason_es,
          // analysis_reason_fr: existing.analysis_reason_fr,
          home_team_id: String(detail.homeTeam.id),
          away_team_id: String(detail.awayTeam.id),
          league_id: String(detail.league),
          matchdate: detail.dateLabel && detail.time 
            ? new Date(`${detail.dateLabel}T${detail.time}`).toISOString() 
            : detail.dateLabel 
              ? new Date(detail.dateLabel).toISOString() 
              : new Date().toISOString(),
        };

        await supabase.from('match_predictions').insert(predictionData);
        
        // 3. 6초 대기 후 노출
        await new Promise(resolve => setTimeout(resolve, 6000));
        
        setMatchPrediction(predictionData as any);
        setIsPredicting(false);
        isPredictingRef.current = false;
        return;
      }

      let newsContext = '';
      try {
        const { news } = await fetchSupabaseNewsList(lang, 0, 20);
        const relevantNews = news.filter(n => {
          const homeName = detail.homeTeam.name.toLowerCase();
          const awayName = detail.awayTeam.name.toLowerCase();
          const title = n.title.toLowerCase();
          const summary = n.summary.toLowerCase();
          
          return title.includes(homeName) || title.includes(awayName) ||
                 summary.includes(homeName) || summary.includes(awayName) ||
                 (n.teams && n.teams.some((t: any) => t.name.toLowerCase() === homeName || t.name.toLowerCase() === awayName));
        }).slice(0, 4);

        if (relevantNews.length > 0) {
          newsContext = relevantNews.map(n => `- ${n.title}: ${n.summary.substring(0, 80)}`).join('\n');
        }
      } catch (newsError) {
        console.warn("Failed to fetch news context for AI prediction:", newsError);
      }

      const KEY_STATS = ['Ball Possession', 'Total Shots', 'Shots on Goal', 'Corner Kicks', 'Fouls'];
      const statsContext = `Home(${detail.homeTeam.name}): form=${homeForm.join('')}, ${detail.stats?.filter(s => s.homeValue && KEY_STATS.includes(s.label)).map(s => `${s.label}=${s.homeValue}`).join(', ')}
Away(${detail.awayTeam.name}): form=${awayForm.join('')}, ${detail.stats?.filter(s => s.awayValue && KEY_STATS.includes(s.label)).map(s => `${s.label}=${s.awayValue}`).join(', ')}
Venue: ${detail.venue}, League: ${displayLeagueName}`;

      const prediction = await predictMatchResult(
        detail.homeTeam.name,
        detail.awayTeam.name,
        `${newsContext}\n${statsContext}`
      );
      
      if (prediction) {
        const predictionData = {
          post_id: gameId,
          user_id: user.id,
          predicted_winner: prediction.predicted_winner,
          predicted_score: prediction.predicted_score,
          win_probability: prediction.win_probability,
          analysis_reason: prediction.analysis_reason,
          analysis_reason_ko: prediction.analysis_reason_ko,
          // analysis_reason_ja: prediction.analysis_reason_ja,
          // analysis_reason_es: prediction.analysis_reason_es,
          // analysis_reason_fr: prediction.analysis_reason_fr,
          home_team_id: String(detail.homeTeam.id),
          away_team_id: String(detail.awayTeam.id),
          league_id: String(detail.league),
          matchdate: detail.dateLabel && detail.time ? new Date(`${detail.dateLabel}T${detail.time}`).toISOString() : detail.dateLabel ? new Date(detail.dateLabel).toISOString() : new Date().toISOString(),
        };

        await supabase.from('match_predictions').insert(predictionData);
        
        setMatchPrediction(predictionData as any);
      }
    } catch (e: any) {
      console.error(e);
      if (e?.message === 'AI_BOTH_FAILED') {
        setAiErrorModalOpen(true);
      } else {
        showToast('error', t(lang, 'errorAnalysis'));
      }
    } finally {
      setIsPredicting(false);
      isPredictingRef.current = false;
    }
  }, [detail, user, lang, showToast, homeForm, awayForm, displayLeagueName, gameId])

  const renderCommentCard = useCallback((
    comment: CommentWithUser & { is_liked?: boolean },
    isReply = false,
  ) => {
    const isMine = user && user.id === comment.user_id
    const isLiked = comment.is_liked

    return (
      <div
        key={comment.id}
        className={`group/card flex gap-3 md:gap-4 ${isReply ? 'ml-10 md:ml-14 mt-4' : 'mt-8'}`}
      >
        <div className='flex flex-col items-center shrink-0'>
          <div
            className={`relative ${isReply ? 'w-8 h-8' : 'w-10 h-10 md:w-12 md:h-12'} rounded-full overflow-hidden bg-slate-100 border border-gray-100 shadow-sm z-10 ring-4 ring-white`}
          >
            <img
              src={
                comment.user?.avatar_url ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.user?.name || 'U')}&background=random`
              }
              alt=''
              className='w-full h-full object-cover'
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (!target.src.includes('ui-avatars.com')) {
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.user?.name || 'U')}&background=random`;
                }
              }}
            />
          </div>
        </div>

        <div className='flex-1 min-w-0'>
          <div className='flex items-center justify-between mb-1'>
            <div className='flex items-center gap-1.5 min-w-0'>
              <span className='font-black text-slate-900 text-sm truncate uppercase tracking-tight'>
                {comment.user?.name || 'Unknown Scout'}
              </span>
              <span className='text-slate-400 text-[10px] font-medium shrink-0'>
                ·
              </span>
              <span className='text-slate-400 text-[10px] font-medium shrink-0'>
                {calculateTimeAgo(comment.created_at)}
              </span>
            </div>
            <div className='relative'>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setActiveMenuId(
                    activeMenuId === String(comment.id)
                      ? null
                      : String(comment.id),
                  )
                }}
                className='p-1 text-slate-300 md:hover:text-slate-900 transition-colors'
              >
                <MoreVertical size={14} />
              </button>
              {activeMenuId === String(comment.id) && (
                <div className='absolute top-full right-0 mt-1 z-30 animate-in fade-in zoom-in-95 duration-200'>
                  <div className='bg-white rounded-xl border border-gray-100 py-1 min-w-[120px] shadow-xl overflow-hidden'>
                    <button
                      onClick={() => handleReportClick(comment.id)}
                      className='w-full text-left px-3 py-2.5 text-[10px] font-bold text-slate-500 md:hover:bg-slate-50 flex items-center gap-2'
                    >
                      <ShieldAlert size={12} /> {t(lang, 'report')}
                    </button>
                    {isMine && (
                      <button
                        onClick={() => confirmDelete(comment.id)}
                        className='w-full text-left px-3 py-2.5 text-[10px] font-bold text-rose-500 md:hover:bg-rose-50 flex items-center gap-2'
                      >
                        <Trash2 size={12} /> {t(lang, 'delete')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='space-y-3'>
            <p className='text-slate-700 text-[15px] leading-relaxed font-medium'>
              {comment.content}
            </p>
            {comment.image_url && (
              <div className='rounded-2xl md:rounded-[24px] overflow-hidden border border-gray-100 bg-slate-50 max-w-64 shadow-sm'>
                <img
                  src={comment.image_url}
                  alt='Shared media'
                  className='w-full h-auto object-contain cursor-zoom-in md:hover:scale-[1.02] transition-transform'
                  onClick={() => handleOpenImageZoom(comment.image_url!)}
                />
              </div>
            )}
          </div>

          <div className='flex items-center gap-6 mt-4 text-slate-400'>
            <button
              onClick={() => handleLikeToggle(comment.id, !!isLiked)}
              className={`flex items-center gap-1.5 transition-all active:scale-90 ${isLiked ? 'text-rose-500' : 'md:hover:text-rose-500'}`}
            >
              <Heart
                size={16}
                fill={isLiked ? 'currentColor' : 'transparent'}
              />
              <span className='text-[11px] font-black tabular-nums'>
                {comment.likes_count || 0}
              </span>
            </button>
            {!isReply && (
              <button
                onClick={() => handleReplyClick(comment)}
                className='flex items-center gap-1.5 md:hover:text-slate-900 transition-colors'
              >
                <MessageSquare size={16} />
                <span className='text-[11px] font-black uppercase tracking-wider'>
                  {t(lang, 'reply')}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }, [user, activeMenuId, handleReportClick, confirmDelete, handleLikeToggle, handleReplyClick, handleOpenImageZoom, lang])

  if (loading)
    return (
      <FullPageLoader
        isLoading={true}
        icon={Trophy}
        text={t(lang, 'syncingMatchDetail')}
      />
    )

  if (error || !detail)
    return (
      <div className='min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center'>
        <AlertCircle
          size={48}
          className='text-rose-500 mb-4'
        />
        <h2 className='text-xl font-bold text-slate-800'>
          {t(lang, 'articleNotFound')}
        </h2>
        <button
          onClick={onBack}
          className='mt-6 px-6 py-2 bg-slate-900 text-white rounded-full font-bold'
        >
          {t(lang, 'goBack')}
        </button>
      </div>
    )

  const winProbRaw = matchPrediction
    ? Math.round(Number(matchPrediction.win_probability) || 50)
    : 50
  const isHomeWinner =
    matchPrediction?.predicted_winner?.trim() === detail.homeTeam.name?.trim()
  const isAwayWinner =
    matchPrediction?.predicted_winner?.trim() === detail.awayTeam.name?.trim()
  const isDraw = matchPrediction?.predicted_winner === 'Draw'

  const winProbHome = isHomeWinner ? winProbRaw : isDraw ? 50 : 100 - winProbRaw
  const winProbAway = isAwayWinner ? winProbRaw : isDraw ? 50 : 100 - winProbRaw

  const homeName = getTeamName(detail.homeTeam.id, lang, detail.homeTeam.name)
  const awayName = getTeamName(detail.awayTeam.id, lang, detail.awayTeam.name)

  return (
    <div className='min-h-screen bg-white font-sans pb-48 animate-in fade-in duration-500'>
      <SEO
        title={`${homeName} vs ${awayName} - ${displayLeagueName}`}
        description={
          lang === 'ko'
            ? `${homeName}와 ${awayName}의 실시간 스코어, 경기 통계, 라인업 및 AI 분석 리포트를 확인하세요.`
            : `Check out live scores, match stats, lineups, and AI analysis reports for ${homeName} vs ${awayName}.`
        }
      />
      <LoadingBar isLoading={isPosting || commentsLoading || isRefreshing} />
      {pointFeedback && (
        <PointFeedback 
          type={pointFeedback.type} 
          amount={pointFeedback.amount} 
          position={pointFeedback.position}
          lang={lang}
          onComplete={() => setPointFeedback(null)} 
        />
      )}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={executeDelete}
        isLoading={isDeleting}
        title={t(lang, 'deleteCommentTitle')}
        confirmLabel={t(lang, 'delete')}
        cancelLabel={t(lang, 'cancel')}
      />
      <ConfirmModal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onConfirm={handleLoginRedirect}
        title={t(lang, 'loginRequiredTitle')}
        confirmLabel={t(lang, 'signInLabel')}
      />
      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onConfirm={executeReport}
        isLoading={isReporting}
        lang={lang}
      />
      <ConfirmModal
        isOpen={aiErrorModalOpen}
        onClose={() => setAiErrorModalOpen(false)}
        onConfirm={() => setAiErrorModalOpen(false)}
        title={t(lang, 'aiErrorTitle')}
        description={t(lang, 'aiErrorDesc')}
        confirmLabel={t(lang, 'confirm')}
      />
      <ConfirmModal
        isOpen={favoriteModalOpen}
        onClose={handleCloseFavoriteModal}
        onConfirm={handleConfirmFavoriteDefault}
        onSecondaryConfirm={handleNoFavoriteDefault}
        title={t(lang, 'setDefaultTeamTitle')}
        description={t(lang, 'setDefaultTeamDesc')}
        confirmLabel={t(lang, 'yes')}
        cancelLabel={t(lang, 'no')}
        isLoading={favoriteLoading}
        isSecondaryLoading={favoriteSecondaryLoading}
      />
      <PlayerQuickModal
        isOpen={!!selectedPlayerForModal}
        onClose={() => setSelectedPlayerForModal(null)}
        player={selectedPlayerForModal}
        lang={lang}
      />
      <TeamQuickModal
        isOpen={!!selectedTeamForModal}
        onClose={() => setSelectedTeamForModal(null)}
        team={selectedTeamForModal}
        lang={lang}
      />
      {detail && (
        <StandingsModal
          isOpen={isStandingsModalOpen}
          onClose={() => setIsStandingsModalOpen(false)}
          leagueId={String(detail.league)}
          leagueName={detail.leagueName || detail.league || ''}
          leagueLogo={leagueLogo || detail.leagueLogo || ''}
          season={matchSeason}
          allowLeagueChange={false}
          homeTeamId={detail.homeTeam.id}
          awayTeamId={detail.awayTeam.id}
        />
      )}
      <input
        type='file'
        ref={fileInputRef}
        className='hidden'
        accept='image/*'
        onChange={handleImageFileSelect}
      />

      <header className='sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-4 h-[65px] flex items-center justify-between'>
        <div className='flex items-center gap-4 min-w-0 flex-1'>
          <button
            onClick={onBack}
            className='w-10 h-10 flex items-center justify-center rounded-full bg-slate-50 md:hover:bg-slate-100 text-slate-900 transition-all active:scale-90 shrink-0 border border-slate-100'
          >
            <ArrowLeft
              size={20}
              strokeWidth={3}
            />
          </button>
          <div className='flex flex-col min-w-0 pr-4'>
            <h2 className='text-[10px] font-black text-rose-500 uppercase tracking-[0.2em] leading-none mb-1'>
              {displayLeagueName}
            </h2>
            <span className='text-sm font-bold text-slate-900 truncate leading-tight tracking-tight uppercase'>
              {homeName} VS {awayName}
            </span>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          <button
            onClick={handleOpenStandings}
            className='w-10 h-10 flex items-center justify-center rounded-full bg-white text-slate-400 md:hover:text-slate-900 border border-slate-100 transition-all active:scale-90'
            title={t(lang, 'standings')}
          >
            <Trophy
              size={18}
              strokeWidth={2.5}
            />
          </button>
          {detail.status === 'Live' && (
            <button
              onClick={() => load(true)}
              disabled={isRefreshing}
              className='w-10 h-10 flex items-center justify-center rounded-full bg-white text-slate-400 md:hover:text-rose-500 border border-slate-100 transition-all active:scale-90 shadow-sm'
              title={t(lang, 'refreshLiveData')}
            >
              <RefreshCw
                size={18}
                className={isRefreshing ? 'animate-spin text-rose-500' : ''}
              />
            </button>
          )}
          <button
            onClick={handleShare}
            className='w-10 h-10 flex items-center justify-center rounded-full bg-white text-slate-400 md:hover:text-slate-900 border border-slate-100 transition-all active:scale-90'
          >
            <Share2
              size={18}
              strokeWidth={2.5}
            />
          </button>
        </div>
      </header>

      <div className='mt-8 flex justify-center'>
        {detail?.venue && (
          <button
            onClick={handleOpenVenueModal}
            className='inline-flex items-center gap-2 bg-gray-50 border border-gray-100 px-4 py-1.5 rounded-full md:hover:bg-white md:hover:border-rose-300 transition-all active:scale-95 group'
          >
            <MapPin
              size={10}
              className='text-rose-500 md:group-hover:scale-110 transition-transform'
            />
            <span className='text-[9px] font-black text-slate-400 md:group-hover:text-slate-600 uppercase tracking-widest truncate max-w-[180px]'>
              {detail.venue}
            </span>
          </button>
        )}
      </div>

      <MatchScoreHeader
        key={`match-header-${favoriteUpdateTrigger}`}
        detail={detail}
        lang={lang}
        homeForm={homeForm}
        awayForm={awayForm}
        onOpenVenue={handleOpenVenueModal}
        onTeamClick={handleTeamClick}
        onRefresh={() => load(true)}
        isRefreshing={isRefreshing}
        onToggleFavorite={handleToggleFavorite}
        onOpenStandings={handleOpenStandings}
      />

      {isScheduled && detail && (
        <div className='mt-5 md:px-6 max-w-[750px] mx-auto'>
          {apiPredictions && (
            <>
              <div className='flex items-center gap-2 mb-6 px-4 md:px-0'>
                <div className='w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center text-white'>
                  <Scale size={16} />
                </div>
                <h2 className='text-xs font-black text-slate-900 uppercase tracking-widest'>
                  {t(lang, 'overallStrength')}
                </h2>
              </div>
              <MatchPredictionGraph
                data={apiPredictions}
                lang={lang}
              />
            </>
          )}
          {h2hData && h2hData.length > 0 && (
            <MatchH2HSection
              h2hData={h2hData}
              lang={lang}
              homeTeamId={detail.homeTeam.id ?? ''}
              awayTeamId={detail.awayTeam.id ?? ''}
            />
          )}
        </div>
      )}

      {(!isScheduled && highlights.length > 0) && (
          <div className="mt-10 px-6">
              <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center text-white"><Play size={16} fill="currentColor" /></div>
                      <h2 className="text-xs font-black text-slate-900 uppercase tracking-widest">{t(lang, 'matchHighlights')}</h2>
                  </div>
                  {currentMatchVideo && <button onClick={() => setActiveVideo(null)} className="text-[10px] font-black text-rose-500 flex items-center gap-1">{t(lang, 'close')} <ChevronUp size={12} /></button>}
              </div>
              {currentMatchVideo && (
                  <div className="mb-4 aspect-video bg-black rounded-3xl overflow-hidden border border-gray-200">
                      <iframe 
                          src={`https://www.youtube.com/embed/${currentMatchVideo.videoId}?autoplay=1&rel=0${currentMatchVideo.currentTime ? `&start=${Math.floor(currentMatchVideo.currentTime)}` : ''}`} 
                          className="w-full h-full" 
                          allow="autoplay; encrypted-media; fullscreen" 
                          allowFullScreen 
                          title="Match Video" 
                      />
                  </div>
              )}
              <div className="flex overflow-x-auto no-scrollbar gap-3 pb-2">
                  {highlights.map((video) => (
                      <div key={video.id} onClick={() => handleSelectVideo(video)} className="flex-shrink-0 w-48 cursor-pointer group">
                          <div className="relative aspect-video rounded-2xl overflow-hidden mb-2">
                              <img src={`https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 md:group-hover:opacity-100 transition-opacity">
                                  <Play size={24} className="text-white fill-white" />
                              </div>
                          </div>
                          <h3 className="text-[10px] font-bold leading-tight line-clamp-2 text-slate-800">{video.title}</h3>
                      </div>
                  ))}
              </div>
          </div>
      )}

      {(isLive || isFinished || isWithinOneHour) && (
        <div className="px-6">
          <div className="overflow-hidden">
            <MatchTimelineTab
              events={detail.events || []}
              lang={lang}
              status={detail.status}
              currentMinute={Number(detail.currentMinute) || 0}
              variant="horizontal"
              homeLogo={detail.homeTeam.logo}
              awayLogo={detail.awayTeam.logo}
              onPlayerClick={(p) => {
                const team = detail.events.find(e => (e.player_id === p.id || e.player === p.name))?.team;
                const teamName = team === 'home' ? detail.homeTeam.name : detail.awayTeam.name;
                const teamLogo = team === 'home' ? detail.homeTeam.logo : detail.awayTeam.logo;
                handlePlayerClick(p, teamName, teamLogo);
              }}
            />
          </div>
        </div>
      )}

      {isScheduled && (
        <div className='mt-12 px-4 md:px-6'>
          <div className='flex items-center gap-2 mb-6'>
            <div className='w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center text-white shadow-md shrink-0'>
              <BrainCircuit size={18} />
            </div>
            <h2 className='text-xs font-black text-slate-900 tracking-widest uppercase leading-none'>
              {t(lang, 'aiMatchPrediction')}
            </h2>
          </div>

          <div className='bg-white rounded-[40px] p-2 border border-slate-100 relative overflow-hidden mb-12'>
            <div className='relative z-10'>
              {matchPrediction ? (
                <div className='animate-in fade-in slide-in-from-bottom-6 duration-700 space-y-6'>
                  

                  {/* 예상 스코어 카드 */}
                  <div className='p-6 flex flex-col items-center justify-between relative overflow-hidden pb-0'>

                    <div className='flex w-full items-center justify-between z-10'>
                      <div className='relative text-center'>
                        <div className='w-16 h-16 md:w-20 md:h-20 p-4 bg-slate-50 rounded-3xl flex items-center justify-center border border-slate-100 transition-transform md:hover:scale-105'>
                          <img
                            src={detail.homeTeam.logo}
                            alt=''
                            className='w-full h-full object-contain'
                          />
                        </div>
                        <span className='text-[10px] font-black text-slate-500 uppercase tracking-wider truncate max-w-[100px] md:max-w-none'>
                          {homeName}
                        </span>
                      </div>
                      <div className='text-center flex flex-col items-center justify-center'>
                        <span className='text-[10px] font-black text-rose-500 uppercase tracking-[0.25em] block mb-3 opacity-80'>
                          {t(lang, 'expectedScore')}
                        </span>
                        <span className='text-5xl md:text-6xl font-black text-slate-900 tracking-wider tabular-nums leading-none'>
                          {matchPrediction.predicted_score}
                        </span>
                      </div>
                      <div className='relative text-center'>
                        <div className='w-16 h-16 md:w-20 md:h-20 p-4 bg-slate-50 rounded-3xl flex items-center justify-center border border-slate-100 transition-transform md:hover:scale-105'>
                          <img
                            src={detail.awayTeam.logo}
                            alt=''
                            className='w-full h-full object-contain'
                          />
                        </div>
                        <span className='text-[10px] font-black text-slate-500 uppercase tracking-wider truncate max-w-[100px] md:max-w-none'>
                          {awayName}
                        </span>
                      </div>
                    </div>
                    
                  </div>

                  {/* 승률 분석 영역 */}
                  <div className='p-6 pt-0'>
                    <div className='flex justify-between items-end'>
                      <div className='flex flex-col gap-2'>
                        <div className='flex items-center gap-2'>
                          {/* <img src={detail.homeTeam.logo} alt="" className="w-5 h-5 object-contain" /> */}
                          {/* <span className='text-[10px] font-black text-slate-500 uppercase tracking-wider truncate max-w-[100px] md:max-w-none'>
                            {homeName}
                          </span> */}
                        </div>
                        <span className='text-3xl font-black text-slate-900 tabular-nums'>
                          {winProbHome}%
                        </span>
                      </div>
                      
                      <div className='flex flex-col items-end gap-2'>
                        <div className='flex items-center gap-2'>
                          {/* <span className='text-[10px] font-black text-slate-500 uppercase tracking-wider truncate max-w-[100px] md:max-w-none'>
                            {awayName}
                          </span> */}
                          {/* <img src={detail.awayTeam.logo} alt="" className="w-5 h-5 object-contain" /> */}
                        </div>
                        <span className='text-3xl font-black text-slate-900 tabular-nums'>
                          {winProbAway}%
                        </span>
                      </div>
                    </div>
                    <div className='h-2.5 w-full bg-slate-200 rounded-full overflow-hidden flex mt-2'>
                      <div
                        className={`h-full transition-all duration-[2000ms] ease-out bg-rose-500`}
                        style={{ width: `${winProbHome}%` }}
                      />
                      <div
                        className={`h-full transition-all duration-[2000ms] ease-out bg-slate-900`}
                        style={{ width: `${winProbAway}%` }}
                      />
                    </div>
                  </div>

                  {/* 전략 분석 텍스트 */}
                  <div className='p-4 relative'>
                    <div className='absolute top-8 right-10 opacity-[0.05]'>
                      <Quote className='text-slate-900 w-12 h-12' />
                    </div>
                    <div className='flex items-center gap-2.5 mb-6'>
                      <div className="w-6 h-6 rounded-lg bg-rose-500 flex items-center justify-center">
                        <Zap
                          size={14}
                          className='text-white fill-white'
                        />
                      </div>
                      <h3 className='text-[11px] font-black text-slate-900 uppercase tracking-widest'>
                        {t(lang, 'tacticalInsight')}
                      </h3>
                    </div>
                    <p className='text-slate-700 text-[15px] md:text-[16px] font-medium leading-[1.8] whitespace-pre-wrap'>
                      {lang === 'ko'
                        ? matchPrediction.analysis_reason_ko
                        // : lang === 'ja'
                        // ? matchPrediction.analysis_reason_ja || matchPrediction.analysis_reason
                        // : lang === 'es'
                        // ? matchPrediction.analysis_reason_es || matchPrediction.analysis_reason
                        // : lang === 'fr'
                        // ? matchPrediction.analysis_reason_fr || matchPrediction.analysis_reason
                        : matchPrediction.analysis_reason}
                    </p>
                  </div>
                </div>
              ) : (
                <div className='text-center py-6'>
                  <p className='text-slate-500 text-xs md:text-sm font-medium leading-relaxed mb-8 max-w-xs mx-auto'>
                    {t(lang, 'aiSimulationDesc')}
                  </p>
                  <button
                    onClick={(e) => handlePredictMatch(e)}
                    disabled={isPredicting}
                    className='w-full max-w-[280px] mx-auto bg-slate-900 text-white py-4 rounded-2xl font-black text-[10px] md:text-xs uppercase tracking-[0.15em] md:hover:bg-rose-500 transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 shadow-md shadow-slate-200 animate-bounce'
                  >
                    {isPredicting ? (
                      <>
                        <AIVoiceWave 
                          color="white"
                          className="scale-90"
                        />
                        {t(lang, 'simulatingScenarios')}
                      </>
                    ) : (
                      <>
                        {t(lang, 'runSimulation')}
                        <Sparkles
                          size={16}
                          className='text-amber-400'
                        />
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
          <SquadBuilder
            game={detail}
            lang={lang}
            season={matchSeason}
            onShareToComments={handleShareSquad}
          />
        </div>
      )}

      {(isLive || isFinished || isWithinOneHour) && (
        <div
          ref={tabsRef}
          className='bg-white border-b border-slate-100 flex justify-center sticky top-[65px] z-30'
        >
          <div className='flex w-full max-w-4xl px-6 h-14 items-center'>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id as any)}
                className={`flex-1 h-full relative flex items-center justify-center text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab.id ? 'text-slate-900' : 'text-slate-300 md:hover:text-slate-600'}`}
              >
                {activeTab === tab.id && (
                  <div className='absolute bottom-0 left-0 right-0 h-1 bg-slate-900 animate-in fade-in duration-300' />
                )}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className='px-6 py-6 pb-20 max-w-[750px] mx-auto min-h-[400px]'>
        {(isLive || isFinished || isWithinOneHour) && activeTab === 'Stats' && (
          <MatchStatsTab
            stats={detail.stats || []}
            lang={lang}
          />
        )}
        {(isLive || isFinished || isWithinOneHour) && activeTab === 'Timeline' && (
          <MatchTimelineTab
            events={detail.events}
            lang={lang}
            status={detail.status}
            currentMinute={Number(detail.currentMinute) || 0}
            onPlayerClick={(p) => {
              const team = detail.events.find(e => (e.player_id === p.id || e.player === p.name))?.team;
              const teamName = team === 'home' ? detail.homeTeam.name : detail.awayTeam.name;
              const teamLogo = team === 'home' ? detail.homeTeam.logo : detail.awayTeam.logo;
              handlePlayerClick(p, teamName, teamLogo);
            }}
          />
        )}
        {(isLive || isFinished || isWithinOneHour) && activeTab === 'Line Up' && (
          <MatchLineupsTab
            detail={detail}
            lang={lang}
            onPlayerClick={handlePlayerClick}
            onCoachClick={handleCoachClick}
          />
        )}

        {((isScheduled && !isWithinOneHour) || activeTab === 'Talk') && (
          <div
            ref={discussionRef}
            className='animate-in fade-in duration-500 min-h-[400px]'
          >
            {commentsLoading ? (
              <div className='py-20 flex flex-col items-center justify-center gap-3'>
                <Loader2 className='animate-spin text-rose-500' />
                <span className='text-[9px] font-black text-gray-400 uppercase tracking-widest animate-pulse'>
                  {t(lang, 'syncingHub')}
                </span>
              </div>
            ) : comments.length === 0 ? (
              <div className='py-32 flex flex-col items-center justify-center text-gray-300 text-center gap-4'>
                <MessageSquare
                  size={48}
                  className='opacity-10'
                />
                <p className='text-xs font-black uppercase tracking-[0.2em] text-gray-400'>
                  {t(lang, 'firstIntelligenceReport')}
                </p>
              </div>
            ) : (
              <div className='space-y-0'>
                {structuredComments.map((rootComment) => (
                  <div
                    key={rootComment.id}
                    className='relative'
                  >
                    {rootComment.replies.length > 0 && (
                      <div className='absolute left-[19px] md:left-[23px] top-12 bottom-6 w-0.5 bg-slate-100 rounded-full z-0'></div>
                    )}
                    {renderCommentCard(rootComment)}
                    {rootComment.replies.map((reply) => (
                      <div
                        key={reply.id}
                        className='relative'
                      >
                        {renderCommentCard(reply, true)}
                      </div>
                    ))}
                  </div>
                ))}
                <div
                  ref={commentsEndRef}
                  className='h-32'
                />
              </div>
            )}
          </div>
        )}
      </div>

      {(isScheduled || activeTab === 'Talk') && (
        <div
          className={`fixed bottom-[65px] md:bottom-0 left-0 right-0 z-50 transition-all duration-700 ease-[0.32,0.72,0,1] flex justify-center pointer-events-none ${isCommentInView || activeTab === 'Talk' || (isScheduled && !isWithinOneHour) ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}
        >
          <div className='w-full max-w-[1200px] flex'>
            <div className='hidden md:block md:w-20 lg:w-64 shrink-0' />
            <div className='flex-1 flex justify-center min-w-0 pointer-events-none'>
              <div className='w-full max-w-[750px] px-4 pb-4 md:pb-6 pointer-events-auto'>
                <div className='relative bg-white/90 backdrop-blur-xl border border-gray-100 rounded-[28px] shadow-[0_-10px_40px_rgba(0,0,0,0.08)] overflow-hidden transition-all duration-300 focus-within:ring-2 focus-within:ring-rose-500/20'>
                  {!user && (
                    <button
                      onClick={() => {
                        sessionStorage.setItem(
                          'redirectAfterLogin',
                          window.location.pathname,
                        )
                        setLoginModalOpen(true)
                      }}
                      className='absolute inset-0 z-[60] w-full h-full cursor-pointer bg-transparent'
                      aria-label='Login required to comment'
                    />
                  )}

                  {isPosting && (
                    <div className='absolute inset-0 z-50 bg-white/80 backdrop-blur-md flex flex-col items-center justify-center gap-3'>
                      <Loader2
                        className='animate-spin text-rose-500'
                        size={24}
                      />
                      <span className='text-[11px] font-black text-rose-600 uppercase tracking-widest'>
                        {t(lang, 'postingComment')}
                      </span>
                    </div>
                  )}

                  {(replyingTo || attachedImageUrl) && (
                    <div className='bg-slate-50/50 border-b border-gray-50 flex flex-col gap-2'>
                      {replyingTo && (
                        <div className='px-4 py-2 flex items-center justify-between border-b border-gray-100/50'>
                          <div className='flex items-center gap-2 text-[10px] font-bold text-slate-500 truncate bg-white px-2 py-1 rounded-lg border border-gray-100'>
                            <CornerDownRight
                              size={10}
                              className='text-indigo-500'
                            />
                            <span>
                              {t(lang, 'replyingTo')}{' '}
                              <span className='text-slate-900 font-black'>
                                {replyingTo.user?.name}
                              </span>
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setReplyingTo(null)
                            }}
                            className='text-slate-300 md:hover:text-rose-500 transition-colors shrink-0 z-10'
                          >
                            <X
                              size={14}
                              strokeWidth={3}
                            />
                          </button>
                        </div>
                      )}

                      {attachedImageUrl && (
                        <div className='p-3 flex justify-start'>
                          <div className='relative group shrink-0 max-w-[120px] w-full aspect-square rounded-2xl overflow-hidden border border-gray-200 shadow-sm bg-white'>
                            <img
                              src={attachedImageUrl}
                              className='w-full h-full object-cover'
                              alt='Preview'
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCancelAttachment()
                              }}
                              className='absolute top-2 right-2 w-7 h-7 bg-slate-900/80 backdrop-blur-md text-white rounded-full flex items-center justify-center shadow-lg z-10 transition-all md:hover:bg-rose-50'
                            >
                              <X
                                size={14}
                                strokeWidth={4}
                              />
                            </button>
                            <div className='absolute inset-0 bg-black/20 opacity-0 md:group-hover:opacity-100 transition-opacity pointer-events-none' />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className='flex items-end gap-2 p-2 pl-3'>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        fileInputRef.current?.click()
                      }}
                      className='w-10 h-10 rounded-full flex items-center justify-center text-slate-400 md:hover:bg-slate-50 md:hover:text-rose-500 transition-all shrink-0 z-10'
                    >
                      <Camera size={20} />
                    </button>

                    <textarea
                      ref={commentInputRef}
                      className='flex-1 bg-transparent border-none focus:ring-0 text-[14px] font-medium text-slate-800 placeholder:text-slate-300 resize-none py-2.5 h-10 max-h-32 custom-scrollbar focus:outline-none'
                      placeholder={
                        user
                          ? replyingTo
                            ? t(lang, 'typeIntelligenceUpdate')
                            : t(lang, 'joinConversation')
                          : t(lang, 'loginParticipate')
                      }
                      value={newComment}
                      rows={1}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement
                        target.style.height = 'auto'
                        target.style.height = `${target.scrollHeight}px`
                      }}
                      onChange={(e) => setNewComment(e.target.value)}
                      disabled={isPosting || !user}
                    />

                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handlePostComment()
                      }}
                      disabled={
                        (!newComment.trim() && !attachedImageUrl) ||
                        isPosting ||
                        !user
                      }
                      className='w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center md:hover:bg-rose-600 transition-all active:scale-90 disabled:opacity-20 shrink-0 z-10'
                    >
                      {isPosting ? (
                        <Loader2
                          size={16}
                          className='animate-spin'
                        />
                      ) : (
                        <>
                          <Send
                            size={18}
                            className='ml-0.5'
                          />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='hidden md:block w-80 shrink-0' />
          </div>
        </div>
      )}

      {isVenueModalOpen && (
        <div
          className={`fixed inset-0 z-[300] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isVenueAnimating ? 'opacity-100' : 'opacity-0'}`}
        >
          <div
            className='absolute inset-0 bg-black/60 backdrop-blur-[4px]'
            onClick={handleCloseVenueModal}
          ></div>
          <div
            className={`relative bg-white w-full h-[380px] sm:h-[420px] sm:max-w-[420px] rounded-t-[48px] sm:rounded-[48px] overflow-hidden flex flex-col transition-transform duration-700 ease-[0.32,0.72,0,1] ${isVenueAnimating ? 'translate-y-0' : 'translate-y-full'}`}
          >
            <div className='relative flex-1 group'>
              {isVenueLoading ? (
                <div className='w-full h-full flex flex-col items-center justify-center bg-slate-900 gap-3'>
                  <Loader2
                    className='animate-spin text-rose-500'
                    size={32}
                  />
                  <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse'>
                    {t(lang, 'syncingMatchIntelDesc')}
                  </p>
                </div>
              ) : (
                <>
                  {venueDetails?.image ? (
                    <img
                      src={venueDetails.image}
                      alt=''
                      className='w-full h-full object-cover'
                    />
                  ) : (
                    <div className='w-full h-full flex items-center justify-center bg-slate-900'>
                      <MapIcon
                        size={80}
                        className='text-slate-700 opacity-20'
                      />
                    </div>
                  )}
                  <button
                    onClick={handleCloseVenueModal}
                    className='absolute top-6 right-6 z-40 w-10 h-10 bg-black/20 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10 md:hover:bg-black/40 transition-all'
                  >
                    <X
                      size={20}
                      strokeWidth={3}
                    />
                  </button>
                  <div className='absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent'></div>
                  <div className='absolute bottom-10 left-8 right-8 z-30 animate-in fade-in slide-in-from-bottom-2 duration-700'>
                    <div className='flex flex-wrap gap-2.5'>
                      <div className='bg-white/10 backdrop-blur-md px-4 py-2 rounded-2xl flex items-center gap-2.5 border border-white/10'>
                        <Users2
                          size={12}
                          className='text-rose-400'
                        />
                        <span className='text-[12px] font-black text-white tabular-nums tracking-tighter'>
                          {venueDetails?.capacity
                            ? Number(venueDetails.capacity).toLocaleString()
                            : '--'}{' '}
                          Cap.
                        </span>
                      </div>
                    </div>
                    <h2 className='text-3xl sm:text-4xl font-black text-white leading-[1.1] my-3 tracking-tight drop-shadow-lg'>
                      {venueDetails?.name || detail?.venue}
                    </h2>
                    <div className='flex items-start gap-2 text-white/80 mb-5'>
                      <MapPin
                        size={14}
                        className='text-rose-500 shrink-0 mt-0.5'
                      />
                      <span className='text-[13px] font-bold tracking-tight leading-snug'>
                        {venueDetails?.city}
                        {venueDetails?.address
                          ? `, ${venueDetails.address}`
                          : ''}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ImageZoomModal
        imageUrl={zoomedImage || ''}
        isOpen={!!zoomedImage}
        onClose={handleCloseImageZoom}
      />
    </div>
  )
}
